/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */


import { ellipsis, naturalSort } from '../../utils';
import { formatDate } from '../../utils/date';
import React from 'react';
import Table from '../table/Table';
import {
  getActual,
  getBaseline,
  getTarget
} from '../../selectors/universal-tracker-values';
import {
  UniversalTrackerMin,
  UniversalTrackerPlain
} from '../../types/universalTracker';
import { useDispatch } from 'react-redux';
import { getChangeOfDirection, getChartDataByValues } from '../../utils/charts';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import { UtrTableChart } from './UtrTableChart';
import SimpleTooltip from '../simple-tooltip';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { Button } from 'reactstrap';
import { useAppSelector } from '../../reducers';
import { Row } from 'react-table';
import { UtrTableValue } from './UtrTableValue';

interface UtrTableProps {
  utrv: { loaded: boolean };
  data: UniversalTrackerPlain[];
  rows?: number;
  className?: string;
}

interface RowData {
  utrId: string;
  col1: JSX.Element;
  col2: JSX.Element;
  col3: string;
  col4: JSX.Element;
  col5: JSX.Element;
  col6: JSX.Element;
  col7: JSX.Element;
}

const getClassName = (targetDirection: string, changeDirection: number) => {
  if (changeDirection > 0) {
    const arrowDirection = targetDirection === 'decrease' ? 'fa-caret-down' : 'fa-caret-up';
    return `fas ${arrowDirection} fa-2x text-ThemeSuccessMedium`;
  } else if (changeDirection < 0) {
    const arrowDirection = targetDirection === 'decrease' ? 'fa-caret-up' : 'fa-caret-down';
    return `fas ${arrowDirection} fa-2x text-ThemeDangerMedium`;
  }
  return 'fas fa-circle fa-md text-ThemeAccentMedium';
}

const getChangeDirection = (universalTracker: UniversalTrackerPlain, chartData: any) => {
  if (chartData.length === 0) {
    return 0;
  }

  const targetDirection = universalTracker.targetDirection === 'decrease' ? -1 : 1;
  return getChangeOfDirection(chartData, targetDirection);
};

const sortNumber = (columnKey: keyof RowData) => (a: Row<RowData>, b: Row<RowData>): number => {
  const isReactElement = columnKey !== 'utrId' && columnKey !== 'col3';
  if (isReactElement) {
    const aValue = a?.original[columnKey]?.props?.value ?? -1;
    const bValue = b?.original[columnKey]?.props?.value ?? -1;
    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
  }
  return 0;
}

export const UtrTableView = (props: UtrTableProps) => {

  const { data, utrv, rows: pageSize = 10, className } = props;
  const chartValues = useAppSelector(state => state.utrvChartData.data);
  const dispatch = useDispatch();

  // the getActual dispatch relies on this state
  // we need to have a way to track changes so that useMemo refreshes internal state
  const dataStateLastUpdated = useAppSelector(state => state.universalTrackerValues.lastUpdated);

  const getEffectiveDate = React.useCallback((universalTracker: UniversalTrackerMin) => {
    return dispatch(getActual(universalTracker, 'effectiveDate'));
  }, [dispatch]);

  const isUtrvLoaded = utrv && utrv.loaded;

  const tableData: RowData[] = React.useMemo(() => {
    if (!dataStateLastUpdated) {
      // This is just to prevent the compilation error
      // React.useMemo has an unnecessary dependency
      return [];
    }
    return data.map(universalTracker => {
      const chartData = getChartDataByValues(chartValues[universalTracker._id]);
      const changeDirection = getChangeDirection(universalTracker, chartData);
      const targetDirection = universalTracker.targetDirection;
      const className = getClassName(targetDirection as string, changeDirection);

      const baseline = dispatch(getBaseline(universalTracker));
      const actual = dispatch(getActual(universalTracker));
      const target = dispatch(getTarget(universalTracker));

      return {
        utrId: universalTracker._id,
        col1: <i className={className} />,
        col2: <SimpleTooltip text={universalTracker.name}>
          <Button color='link' onClick={() => {
            dispatch(loadUniversalTrackerModal({ universalTrackerId: universalTracker._id }))
          }}>{ellipsis(universalTracker.name, 100)}</Button>
        </SimpleTooltip>,
        col3: formatDate(getEffectiveDate(universalTracker) as unknown as string, 'MMM YYYY'),
        col4: <UtrTableValue universalTracker={universalTracker} utrv={baseline} />,
        col5: <UtrTableValue universalTracker={universalTracker} utrv={actual} />,
        col6: <UtrTableValue universalTracker={universalTracker} utrv={target} />,
        col7: <UtrTableChart universalTracker={universalTracker} changeDirection={changeDirection} />
      }
    });
  }, [data, chartValues, dispatch, getEffectiveDate, dataStateLastUpdated]);

  return (
    <LoadingPlaceholder isLoading={!isUtrvLoaded} height={200}>
      <div className={'react-table-wrapper'}>
        <Table
          responsive={true}
          data={tableData}
          pageSize={pageSize}
          className={'utr-table ' + className}
          sortBy={[{ id: 'universalTracker', desc: false }]}
          columns={[
            {
              Header: ' ',
              disableSortBy: true,
              id: 'arrow',
              cellProps: {
                className: 'text-left text-md-center flex-grow-1 flex-md-grow-0 colArrow order-1'
              },
              accessor: (row: any) => row.col1
            },
            {
              Header: 'Metric Name',
              id: 'universalTracker',
              sortType: (a, b) => {
                return naturalSort(a.original.col2.props.text ?? '', b.original.col2.props.text ?? '');
              },
              cellProps: {
                className: 'text-left rt-utr-name row-break-1 row-md-break-0 order-7 order-md-2'
              },
              accessor: (row: any) => row.col2
            },
            {
              Header: 'Updated',
              id: 'date',
              sortType: (a, b) => {
                return naturalSort(a.original.col3 ?? '', b.original.col3 ?? '');
              },
              cellProps: {
                className: 'numeric text-center d-none d-md-block order-3'
              },
              accessor: (row: any) => row.col3
            },
            {
              Header: 'Baseline',
              id: 'baseline',
              sortType: sortNumber('col4'),
              cellProps: {
                className: 'numeric text-center d-none d-md-block order-4'
              },
              accessor: (row: any) => row.col4
            },
            {
              Header: 'Value',
              id: 'actual',
              sortType: sortNumber('col5'),
              cellProps: {
                className: 'numeric text-center order-5'
              },
              accessor: (row: any) => row.col5
            },
            {
              Header: 'Target',
              id: 'target',
              sortType: sortNumber('col6'),
              cellProps: {
                className: 'numeric text-center order-5'
              },
              accessor: (row: any) => row.col6
            },
            {
              Header: 'Chart',
              id: 'chart',
              disableSortBy: true,
              cellProps: {
                className: 'text-right colChart d-none d-lg-block order-5'
              },
              accessor: (row: any) => row.col7
            },
          ]}
        />
      </div>
    </LoadingPlaceholder>
  );
}
